import React, { useMemo, useState } from 'react';
import type { Transition } from 'framer-motion';
import { AnimatePresence, motion } from 'framer-motion';

import { twMerge } from 'tailwind-merge';
import type { Maybe } from '@soundxyz/utils';
import type { PostModalSource } from '@soundxyz/utils/analytics';
import { useStableCallback } from '@components-shared/src/hooks/useStableCallback';
import { Button } from '@components/main-components/button/Button';
import { StandardModal } from '@components/main-components/modals/StandardModal';
import { FeedPostModalMain } from '@components/modals/FeedPostModal/FeedPostModalMain';
import { FeedPostModalSoundSelect } from '@components/modals/FeedPostModal/FeedPostModalSoundSelect';
import { useFeedPost } from '@components/modals/FeedPostModal/useFeedPost';
import { useModal } from '@context/modal';
import { useToast } from '@context/toast';
import { type FeedPostModalMainSoundFragment } from '@graphql/reactQuery';
import { DEFAULT_ERROR_TOAST_MESSAGE_TRY_AGAIN_LATER, POST_MAX_CHARACTERS } from '@utils/constants';
import { AfterFeedPostModal } from './AfterFeedPost';

enum FeedPostModalStep {
  Main,
  SoundSelect,
}

const TRANSITION: Transition = {
  duration: 0.25,
  ease: 'easeInOut',
};

export function FeedPostModal({
  releaseFrag: initialReleaseFrag,
  sourceLocation,
}: {
  sourceLocation: PostModalSource;
  releaseFrag?: Maybe<FeedPostModalMainSoundFragment>;
}) {
  const { closeModal, openModal } = useModal();
  const [hasOverflow, setHasOverflow] = useState(false);
  const [step, setStep] = useState(FeedPostModalStep.Main);
  const [postCreated, setPostCreated] = useState(false);

  const preselectedRelease: FeedPostModalMainSoundFragment | undefined = initialReleaseFrag
    ? undefined
    : undefined;

  const [releaseFrag, setReleaseFrag] = useState<FeedPostModalMainSoundFragment | undefined>(
    initialReleaseFrag || preselectedRelease,
  );
  const [selectedRelease, setSelectedRelease] = useState<
    FeedPostModalMainSoundFragment | undefined
  >(preselectedRelease);

  const bodyRef = React.useRef<HTMLDivElement>(null);
  const [postText, setPostText] = useState('');
  const { openToast } = useToast();

  const isTextTooLong = postText.length > POST_MAX_CHARACTERS;

  const submitEnabled = postText.length > 0 && releaseFrag && !isTextTooLong;

  const { isLoading: isMutating, mutateAsync } = useFeedPost();

  const handleSubmitClicked = useStableCallback(async () => {
    if (!releaseFrag) {
      return;
    }
    try {
      const result = await mutateAsync({
        input: {
          content: postText,
          releaseId: releaseFrag.id,
        },
      });
      if (result.data.createReleasePost.__typename === 'MutationCreateReleasePostSuccess') {
        openModal('AFTER_RELEASE_FEED_POST', {
          body: (
            <AfterFeedPostModal
              release={releaseFrag}
              releasePostWebappUri={result.data.createReleasePost.data.webappUri}
              sourceLocation={sourceLocation}
              postId={result.data.createReleasePost.data.id}
            />
          ),
        });

        setPostCreated(true);
      } else {
        openToast({ text: DEFAULT_ERROR_TOAST_MESSAGE_TRY_AGAIN_LATER, variant: 'error' });
      }
    } catch (error) {
      openToast({ text: DEFAULT_ERROR_TOAST_MESSAGE_TRY_AGAIN_LATER, variant: 'error' });
    }
  });

  const title = useMemo(
    () => (step === FeedPostModalStep.Main ? 'Write a Post' : 'Search all sounds'),
    [step],
  );

  React.useEffect(() => {
    if (!bodyRef.current) {
      return;
    }
    setHasOverflow(bodyRef.current.clientHeight < bodyRef.current.scrollHeight);
  }, [bodyRef]);

  const stepMainCTA = (
    <div className="flex w-full gap-3 pb-3">
      <Button
        fullWidth
        onClick={() => {
          // if they have post text or a sound selected, show a confirmation modal
          if (
            postText.length &&
            confirm('Are you sure? You changes will not be saved.') === false
          ) {
            return;
          }

          closeModal();
        }}
        label="Cancel"
        disabled={postCreated}
        className="flex-1"
        variant="secondary"
      />
      <Button
        fullWidth
        disabled={!submitEnabled || postCreated}
        label="Post"
        className="flex-1"
        loading={isMutating}
        variant="primary"
        onClick={handleSubmitClicked}
        type="submit"
      />
    </div>
  );
  const stepSoundSelectCTA = (
    <div className="relative z-above1 flex w-full gap-3 pb-3 ">
      <Button
        variant="secondary"
        fullWidth
        className="flex-1"
        label="Back"
        onClick={() => {
          setStep(FeedPostModalStep.Main);
        }}
      />
      <Button
        variant="primary"
        fullWidth
        className="flex-1"
        label="Select"
        disabled={!Boolean(selectedRelease)}
        onClick={() => {
          // don't really need this because the button is disabled
          // when there is no selected release
          // but just in case
          if (selectedRelease) {
            setReleaseFrag(selectedRelease);
            setStep(FeedPostModalStep.Main);
          }
        }}
      />
    </div>
  );

  return (
    <StandardModal
      ref={bodyRef}
      closeModal={closeModal}
      bodyClassName={twMerge(
        'px-0',
        step === FeedPostModalStep.SoundSelect
          ? 'overflow-hidden'
          : 'overflow-hidden md:overflow-auto',
      )}
      containerClassName="pb-0"
      bodyWithoutPadding
      bodyWithoutPaddingBottom
      showHeaderShadow={hasOverflow}
      title={title}
      callToAction={step === FeedPostModalStep.Main ? stepMainCTA : stepSoundSelectCTA}
      body={
        <div className="h-72 md:h-64">
          <AnimatePresence initial={false} mode="popLayout">
            {step === FeedPostModalStep.Main && (
              <motion.div
                initial={{ x: '-100%' }}
                animate={{ x: 0 }}
                exit={{ x: '-100%' }}
                key="main"
                transition={TRANSITION}
                className="flex h-full w-full flex-col gap-6 px-4 pb-4 lt:px-6 lt:pb-6"
              >
                <FeedPostModalMain
                  allowEdit={!Boolean(initialReleaseFrag)}
                  onPostTextChange={setPostText}
                  postText={postText}
                  onChangeSoundClicked={() => {
                    setStep(FeedPostModalStep.SoundSelect);
                  }}
                  release={releaseFrag}
                  isTextTooLong={isTextTooLong}
                />
              </motion.div>
            )}
            {step === FeedPostModalStep.SoundSelect && (
              <motion.div
                //scroll to top when we switch to this step

                initial={{ x: '100%', opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                exit={{ x: '100%' }}
                key="sound-select"
                transition={TRANSITION}
                className="flex h-full w-full flex-col gap-6 pb-4  lt:pb-6"
              >
                <FeedPostModalSoundSelect
                  selectedRelease={selectedRelease}
                  setSelectedRelease={setSelectedRelease}
                />
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      }
    />
  );
}
